import { useEffect, useState, useMemo } from "react";
import { Platform } from "react-native";
import { useTheme } from "styled-components/native";
import { usePdpLayout } from "@redux/slices/pdpLayoutSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ProductCategory } from "@utils/constants";
import { isParcel } from "@utils/product/productValidators";

interface UsePdpButtonsProps {
  product_type: ProductCategory;
  priceMode: number;
  itemDetail: any;
  isCustomApp: boolean;
  enableJewelryBuilder: boolean;
  enterSecondFlow: boolean;
  getAddToJewelryOptionsData: any;
}

export const usePdpButtons = ({
  product_type,
  priceMode,
  itemDetail,
  isCustomApp,
  enableJewelryBuilder,
  enterSecondFlow,
  getAddToJewelryOptionsData,
}: UsePdpButtonsProps) => {
  const theme = useTheme();
  const pdpLayout = usePdpLayout();
  const userInfo = useUserInfo();
  const [bottomButtons, setBottomButtons] = useState<any[]>([]);

  const showBulkRequest = useMemo(
    () =>
      userInfo?.showBulkRequestFeature(
        product_type,
        userInfo?.user?.price_mode
      ),
    [product_type, userInfo]
  );

  const showOrderCartButton = useMemo(
    () => userInfo?.getShoppingCartButtonVisibility(),
    [userInfo]
  );

  useEffect(() => {
    const isJewelryProduct = product_type === ProductCategory.JEWELRY;
    const isCustomizableJewelry =
      isJewelryProduct && itemDetail?.is_customizable === "jewelry_builder";
    const platform =
      enableJewelryBuilder && theme.isTablet
        ? "web"
        : Platform.OS === "web" && !theme.isMobile
        ? "web"
        : "mobile";

    let mBottomButtons =
      isCustomApp && isParcel(itemDetail)
        ? pdpLayout[product_type]?.parcels?.bottom_buttons?.[platform]
        : pdpLayout[product_type]?.bottom_buttons?.[platform];

    let buttons = { ...mBottomButtons };

    if (buttons && Object.keys(buttons).length) {
      if (
        !theme.isMobile &&
        itemDetail?.custom_design_configuration &&
        !buttons?.total_price
      ) {
        buttons.total_price = {
          icon: {
            data: "",
            type: "font",
          },
          name: "total_price",
          type: 0,
          label: "productDetailPage.totalPrice.button.text",
          order: 0,
          category: "diam",
          description: "bottom_right",
          price_modes: [2, 1],
          segment_identifier: "total_price",
        };
      }

      let buttonsArray = Object.keys(buttons)
        .filter(
          (button) =>
            buttons[button].price_modes.length > 0 &&
            buttons[button].price_modes.includes(priceMode) &&
            (buttons[button]?.name === "add_to_bulk"
              ? showBulkRequest
              : true) &&
            (buttons[button]?.name === "add_to_cart"
              ? showOrderCartButton
              : true) &&
            (buttons[button]?.name === "jewelry_builder_button"
              ? isJewelryProduct
                ? isCustomizableJewelry
                : enterSecondFlow
                ? true
                : getAddToJewelryOptionsData?.data?.add_to_jewelry?.options
                    .length > 0
              : true)
        )
        .sort((a, b) => {
          const descPriority: { [key: string]: number } = {
            bottom_left: 0,
            bottom_right: 1,
          };
          if (buttons[a].description !== buttons[b].description) {
            return (
              descPriority[buttons[a].description] -
              descPriority[buttons[b].description]
            );
          }
          return buttons[a].order - buttons[b].order;
        })
        .map((button) => {
          if (buttons[button]?.name === "add_to_cart" && itemDetail?.in_cart) {
            return {
              ...buttons[button],
              name: "added_to_cart",
            };
          } else if (
            buttons[button].name === "request_options" &&
            buttons[button].options.add_to_cart &&
            itemDetail?.in_cart
          ) {
            return {
              ...buttons[button],
              options: {
                ...buttons[button].options,
                add_to_cart: {
                  ...buttons[button].options.add_to_cart,
                  name: "added_to_cart",
                },
              },
            };
          }
          return {
            ...buttons[button],
            label: buttons[button].label,
          };
        });

      const groupedButtons = buttonsArray.reduce((acc: any, button: any) => {
        if (!acc[button.description]) {
          acc[button.description] = [];
        }
        acc[button.description].push(button);
        return acc;
      }, {});

      groupedButtons.bottom_left = groupedButtons.bottom_left || [];
      groupedButtons.bottom_right = groupedButtons.bottom_right || [];

      buttonsArray = [groupedButtons.bottom_left, groupedButtons.bottom_right];
      if (
        itemDetail?.company_identifier &&
        userInfo.companyIdentifierEnabled()
      ) {
        buttonsArray = buttonsArray.map((group) =>
          group.filter(
            (i: any) =>
              !["jewelry_builder_button", "add_to_bulk"].includes(i.name)
          )
        );
      }
      setBottomButtons(buttonsArray);
    }
  }, [
    pdpLayout,
    priceMode,
    itemDetail,
    getAddToJewelryOptionsData?.data?.add_to_jewelry?.options.length,
    isCustomApp,
    enableJewelryBuilder,
    theme.isTablet,
    theme.isMobile,
    product_type,
    showBulkRequest,
    showOrderCartButton,
    enterSecondFlow,
  ]);

  const requestOptionsButton = useMemo(
    () =>
      bottomButtons
        ?.flat()
        ?.find((button) => button?.name === "request_options"),
    [bottomButtons]
  );

  return {
    bottomButtons,
    requestOptionsButton,
  };
};
